import React, { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import JourneyPreview from "@/components/JourneyPreview";
import SimpleEmailPreview from "@/components/SimpleEmailPreview";
import { Bell, User, CreditCard, Save, ShieldAlert, Loader2, ShoppingBag, Package, Truck, AlertCircle, ChevronRight, BellRing, BellOff, Send } from "lucide-react";
import { toast, Toaster } from "sonner";
import { enablePush, disablePush, getPushStatus, sendTestPush, isPushSupported, pushAvailability } from "@/lib/push";
import { useT } from "@/lib/i18n";

const LEVELS = [
  { v: "debutant", label: "Découverte" },
  { v: "intermediaire", label: "Amateur éclairé" },
  { v: "avance", label: "Expert" },
];

const INTERESTS = ["Bourgogne", "Bordeaux", "Champagne", "Vallée du Rhône", "Loire", "Italie", "Espagne", "Spiritueux", "Bière", "Œnotourisme"];

export default function Account() {
  const { user, logout } = useAuth();
  const { lang } = useT();
  const navigate = useNavigate();
  const [profile, setProfile] = useState({ level: "intermediaire", interests: [], notify_email: true });
  const [notif, setNotif] = useState({ email_daily_lesson: true, email_weekly_digest: true, email_product_news: true, push_enabled: false, journey_content_types: ["info"] });
  const [billing, setBilling] = useState({ credits: 0, subscription_active: false });
  const [showDelete, setShowDelete] = useState(false);
  const [deletePw, setDeletePw] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [regionZones, setRegionZones] = useState([]);  // [{slug,label}, …] catalog

  useEffect(() => {
    if (!user) { navigate("/login?next=/account"); return; }
    api.get("/me/profile").then((r) => setProfile({ ...profile, ...r.data })).catch(() => {});
    api.get("/me/notifications").then((r) => setNotif(r.data)).catch(() => {});
    api.get("/me/billing").then((r) => setBilling(r.data)).catch(() => {});
    api.get(`/me/notifications/journey/regions?lang=${encodeURIComponent(lang || "fr")}`)
      .then((r) => setRegionZones(Array.isArray(r.data?.zones) ? r.data.zones : []))
      .catch(() => setRegionZones([]));
    api.get("/shop/orders/mine")
      .then((r) => setOrders(Array.isArray(r.data?.orders) ? r.data.orders : []))
      .catch(() => setOrders([]))
      .finally(() => setOrdersLoading(false));
  }, [user]); // eslint-disable-line

  // Refresh the region catalog whenever the UI language changes.
  useEffect(() => {
    if (!user) return;
    api.get(`/me/notifications/journey/regions?lang=${encodeURIComponent(lang || "fr")}`)
      .then((r) => setRegionZones(Array.isArray(r.data?.zones) ? r.data.zones : []))
      .catch(() => {});
  }, [lang, user]);

  const saveProfile = async () => {
    await api.put("/me/profile", { ...profile, onboarded: true });
    toast.success("Profil enregistré");
  };
  const saveNotif = async () => {
    await api.put("/me/notifications", notif);
    toast.success("Préférences enregistrées");
  };
  const toggleInterest = (i) => {
    setProfile((p) => ({ ...p, interests: p.interests?.includes(i) ? p.interests.filter((x) => x !== i) : [...(p.interests || []), i] }));
  };

  const deleteAccount = async () => {
    if (!deletePw) { toast.error("Mot de passe requis"); return; }
    setDeleting(true);
    try {
      await api.post("/auth/delete-account", { password: deletePw });
      toast.success("Votre compte a été supprimé. Merci d'avoir fait partie de l'aventure.");
      logout();
      navigate("/");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Suppression impossible");
    } finally { setDeleting(false); }
  };

  if (!user) return null;

  return (
    <div className="max-w-3xl mx-auto px-5 md:px-10 py-12 md:py-16">
      <Toaster theme="light" />
      <span className="chip-pink">Mon compte</span>
      <h1 className="font-display text-4xl text-[#1F3A5F] mt-4 leading-[1.05]">{user.name}</h1>
      <p className="text-[#8B95A1] text-sm mt-1">{user.email}</p>

      <section className="mt-10">
        <div className="flex items-center gap-2 mb-4"><User className="w-4 h-4 text-[#E5276A]" /><h2 className="font-display text-2xl text-[#1F3A5F]">Profil & micro-cours</h2></div>
        <div className="app-card p-6 space-y-5">
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-[#E5276A] font-bold mb-2">Niveau</div>
            <div className="flex flex-wrap gap-2">
              {LEVELS.map((l) => (
                <button key={l.v} onClick={() => setProfile({ ...profile, level: l.v })}
                  className={`px-3.5 py-1.5 rounded-full text-sm font-bold border ${profile.level === l.v ? "bg-[#E5276A] text-white border-[#E5276A]" : "bg-white text-[#1F3A5F] border-[#1F3A5F]/12"}`}>{l.label}</button>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-[#E5276A] font-bold mb-2">Centres d&apos;intérêt</div>
            <div className="flex flex-wrap gap-2">
              {INTERESTS.map((i) => (
                <button key={i} onClick={() => toggleInterest(i)}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold border ${profile.interests?.includes(i) ? "bg-[#1F3A5F] text-white border-[#1F3A5F]" : "bg-white text-[#1F3A5F] border-[#1F3A5F]/12"}`}>{i}</button>
              ))}
            </div>
          </div>
          <button onClick={saveProfile} data-testid="profile-save" className="btn-primary"><Save className="w-4 h-4" /> Enregistrer</button>
        </div>
      </section>

      <section className="mt-10">
        <div className="flex items-center gap-2 mb-4"><Bell className="w-4 h-4 text-[#E5276A]" /><h2 className="font-display text-2xl text-[#1F3A5F]">Notifications</h2></div>

        {/* ─── Card 1 — geoVINUM Journey (email personnalisé) ─── */}
        <div className="app-card p-6" data-testid="notif-card-journey">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="inline-flex items-center gap-1.5 chip-pink"><Bell className="w-3 h-3" /> Personnalisé</div>
              <h3 className="font-display text-xl text-[#1F3A5F] mt-2">geoVINUM Journey</h3>
              <p className="text-sm text-[#4A5568] mt-1">
                Un email <b>sur mesure</b> qui regroupe toutes les sections cochées ci-dessous, envoyé au rythme que vous choisissez.
              </p>
            </div>
            <input type="checkbox" data-testid="notif-email_daily_lesson"
              checked={!!notif.email_daily_lesson}
              onChange={(e) => setNotif({ ...notif, email_daily_lesson: e.target.checked })}
              className="w-5 h-5 accent-[#E5276A] mt-1 shrink-0" />
          </div>

          {notif.email_daily_lesson && (
            <div className="pt-4 mt-4 border-t border-[#1F3A5F]/8" data-testid="journey-content-types">
              <div className="text-[11px] uppercase tracking-widest text-[#8B95A1] font-bold mb-2">Contenu de votre bulletin</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {[
                  { k: "info",        label: "Une info / micro-leçon", hint: "Focus d'un sujet œno, 100-140 mots." },
                  { k: "appellation", label: "Une appellation",         hint: "Extrait de la fiche + lien pour approfondir." },
                  { k: "producer",    label: "Un producteur",           hint: "Story du domaine + lien vers la fiche." },
                  { k: "cepage",      label: "Un cépage",               hint: "Origine, arômes et régions phares." },
                ].map((opt) => {
                  const enabled = (notif.journey_content_types || []).includes(opt.k);
                  return (
                    <label
                      key={opt.k}
                      data-testid={`journey-type-${opt.k}`}
                      className={`flex items-start gap-2 rounded-xl border p-3 cursor-pointer transition-colors ${
                        enabled ? "border-[#E5276A] bg-[#FFF8FB]" : "border-[#1F3A5F]/10 hover:border-[#E5276A]/40"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={enabled}
                        onChange={(e) => {
                          const cur = new Set(notif.journey_content_types || []);
                          if (e.target.checked) cur.add(opt.k); else cur.delete(opt.k);
                          if (cur.size === 0) cur.add("info");
                          setNotif({ ...notif, journey_content_types: Array.from(cur) });
                        }}
                        className="w-4 h-4 mt-0.5 accent-[#E5276A]"
                      />
                      <div className="min-w-0">
                        <div className="text-sm font-bold text-[#1F3A5F]">{opt.label}</div>
                        <div className="text-[11px] text-[#4A5568] leading-tight">{opt.hint}</div>
                      </div>
                    </label>
                  );
                })}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4" data-testid="journey-cadence">
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-[#8B95A1]">Fréquence d&apos;envoi</label>
                  <select
                    value={notif.journey_frequency || "weekly"}
                    onChange={(e) => setNotif({ ...notif, journey_frequency: e.target.value })}
                    data-testid="journey-frequency"
                    className="mt-1 w-full px-3 py-2 rounded-lg border border-[#1F3A5F]/15 bg-white text-sm text-[#1F3A5F] focus:border-[#E5276A] outline-none"
                  >
                    <option value="daily">Chaque jour</option>
                    <option value="weekly">Chaque lundi</option>
                    <option value="monthly">Le 1er de chaque mois</option>
                    <option value="off">Désactivé</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-[#8B95A1]">Heure d&apos;envoi (UTC)</label>
                  <select
                    value={notif.journey_time || "08:00"}
                    onChange={(e) => setNotif({ ...notif, journey_time: e.target.value })}
                    data-testid="journey-time"
                    className="mt-1 w-full px-3 py-2 rounded-lg border border-[#1F3A5F]/15 bg-white text-sm text-[#1F3A5F] focus:border-[#E5276A] outline-none"
                  >
                    {Array.from({ length: 24 }, (_, i) => {
                      const h = String(i).padStart(2, "0") + ":00";
                      return <option key={h} value={h}>{h}</option>;
                    })}
                  </select>
                </div>
              </div>

              <JourneyPreview selectedTypes={notif.journey_content_types} />

              {/* ─── Region focus (optional) ─── */}
              <div className="pt-4 mt-4 border-t border-[#1F3A5F]/8" data-testid="journey-regions">
                <div className="text-[11px] uppercase tracking-widest text-[#8B95A1] font-bold mb-1">
                  {lang === "fr" ? "Focus géographique"
                    : lang === "pt" ? "Foco geográfico"
                    : lang === "es" ? "Foco geográfico"
                    : "Geographic focus"}
                </div>
                <div className="text-[12px] text-[#4A5568] mb-3">
                  {lang === "fr" ? "Choisissez une ou plusieurs zones pour recevoir uniquement ces appellations. Laissez vide pour toutes les régions."
                    : lang === "pt" ? "Escolhe uma ou mais zonas para receber apenas essas denominações. Deixa vazio para todas as regiões."
                    : lang === "es" ? "Elige una o varias zonas para recibir solo esas denominaciones. Deja vacío para todas las regiones."
                    : "Pick one or more zones to only receive those appellations. Leave empty for all regions."}
                </div>
                <div className="flex flex-wrap gap-2">
                  {regionZones.map((z) => {
                    const selected = Array.isArray(notif.journey_regions) && notif.journey_regions.includes(z.slug);
                    return (
                      <button
                        type="button"
                        key={z.slug}
                        data-testid={`journey-region-${z.slug}`}
                        onClick={() => {
                          const cur = new Set(notif.journey_regions || []);
                          if (selected) cur.delete(z.slug); else cur.add(z.slug);
                          setNotif({ ...notif, journey_regions: Array.from(cur) });
                        }}
                        className={`px-3 py-1.5 rounded-full text-sm font-semibold transition-colors border ${
                          selected
                            ? "bg-[#E5276A] text-white border-[#E5276A]"
                            : "bg-white text-[#1F3A5F] border-[#1F3A5F]/15 hover:border-[#E5276A]/60"
                        }`}
                      >
                        {z.label}
                      </button>
                    );
                  })}
                </div>
                {Array.isArray(notif.journey_regions) && notif.journey_regions.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setNotif({ ...notif, journey_regions: [] })}
                    className="mt-3 text-[12px] text-[#8B95A1] hover:text-[#E5276A] underline"
                    data-testid="journey-regions-clear"
                  >
                    {lang === "fr" ? "Tout réinitialiser (toutes les régions)"
                      : lang === "pt" ? "Repor tudo (todas as regiões)"
                      : lang === "es" ? "Restablecer todo (todas las regiones)"
                      : "Reset all (every region)"}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* ─── Card 2 — Digest hebdomadaire ─── */}
        <div className="app-card p-6 mt-4" data-testid="notif-card-digest">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="inline-flex items-center gap-1.5 chip-pink">Hebdo</div>
              <h3 className="font-display text-xl text-[#1F3A5F] mt-2">Digest hebdomadaire</h3>
              <p className="text-sm text-[#4A5568] mt-1">
                Chaque dimanche, un <b>récap de la semaine</b> sur geoVINUM : nouveaux blocs Atlas publiés, parcours Academy ajoutés, région à l&apos;honneur et rappel de vos Study Schedule.
              </p>
            </div>
            <input type="checkbox" data-testid="notif-email_weekly_digest"
              checked={!!notif.email_weekly_digest}
              onChange={(e) => setNotif({ ...notif, email_weekly_digest: e.target.checked })}
              className="w-5 h-5 accent-[#E5276A] mt-1 shrink-0" />
          </div>
        <SimpleEmailPreview endpoint={`/me/notifications/digest/preview?lang=${lang}`} testId="digest-preview" />
        </div>

        {/* ─── Card 3 — Newsletter mensuelle ─── */}
        <div className="app-card p-6 mt-4" data-testid="notif-card-newsletter">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="inline-flex items-center gap-1.5 chip-pink">Mensuelle</div>
              <h3 className="font-display text-xl text-[#1F3A5F] mt-2">Newsletter geoVINUM</h3>
              <p className="text-sm text-[#4A5568] mt-1">
                Le <b>1er de chaque mois</b>, la newsletter éditoriale geoVINUM : la région du mois, des appellations à découvrir, les nouveautés Atlas et Academy et une anecdote — rédigée à partir du contenu geoVINUM.
              </p>
            </div>
            <input type="checkbox" data-testid="notif-email_product_news"
              checked={!!notif.email_product_news}
              onChange={(e) => setNotif({ ...notif, email_product_news: e.target.checked })}
              className="w-5 h-5 accent-[#E5276A] mt-1 shrink-0" />
          </div>
          <SimpleEmailPreview endpoint={`/me/notifications/newsletter/preview?lang=${lang}`} testId="newsletter-preview" />
        </div>

        {/* ─── Card 4 — Push notifications ─── */}
        <div className="app-card p-6 mt-4" data-testid="notif-card-push">
          <PushToggle />
        </div>

        <div className="pt-4">
          <button onClick={saveNotif} className="btn-primary"><Save className="w-4 h-4" /> Enregistrer</button>
        </div>
      </section>

      {/* Mes commandes Shop */}
      <section className="mt-10" data-testid="account-orders-section">
        <div className="flex items-center gap-2 mb-4">
          <ShoppingBag className="w-4 h-4 text-[#E5276A]" />
          <h2 className="font-display text-2xl text-[#1F3A5F]">Mes commandes</h2>
        </div>
        <div className="app-card p-6">
          {ordersLoading ? (
            <div className="flex items-center gap-2 text-[#8B95A1]"><Loader2 className="w-4 h-4 animate-spin" /> Chargement…</div>
          ) : orders.length === 0 ? (
            <div className="text-center py-6" data-testid="account-orders-empty">
              <Package className="w-10 h-10 mx-auto text-[#E5276A]/40 mb-2" />
              <p className="text-sm text-[#4A5568]">Aucune commande pour le moment.</p>
              <button onClick={() => navigate("/shop")} data-testid="account-orders-shop-cta"
                className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl border border-[#1F3A5F]/15 text-sm font-bold text-[#1F3A5F] hover:border-[#E5276A]"
                aria-label="Aller à la boutique">
                Découvrir la boutique →
              </button>
            </div>
          ) : (
            <div className="divide-y divide-[#1F3A5F]/8">
              {orders.map((o) => (
                <OrderRow key={o.session_id} order={o} onView={() => navigate(`/shop/order?session_id=${o.session_id}`)} />
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="mt-10">
        <div className="flex items-center gap-2 mb-4"><CreditCard className="w-4 h-4 text-[#E5276A]" /><h2 className="font-display text-2xl text-[#1F3A5F]">Crédits & abonnement</h2></div>
        <div className="app-card p-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="text-3xl font-display text-[#E5276A]">{billing.credits}</div>
              <div className="text-xs text-[#8B95A1] uppercase tracking-widest">Crédits</div>
            </div>
            <div className="text-right">
              <div className="text-sm font-bold text-[#1F3A5F]">{billing.subscription_active ? `${(billing.plan_label || "Pro")} actif` : "Plan gratuit"}</div>
              <div className="text-xs text-[#8B95A1]">{billing.subscription_active ? "Accès illimité aux outils premium" : "Amateur €29/mois — IA illimitée, builders premium"}</div>
            </div>
          </div>
          <div className="mt-5 flex flex-wrap gap-2">
            <button
              onClick={() => navigate("/billing")}
              data-testid="account-manage-billing"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#1F3A5F] text-white text-sm font-bold hover:bg-[#2A4870]"
            >
              Gérer la facturation
            </button>
            <button
              onClick={() => navigate("/pricing")}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl border border-[#1F3A5F]/15 text-sm font-bold text-[#1F3A5F] hover:border-[#E5276A]"
            >
              {billing.subscription_active ? "Changer de plan" : "Découvrir les plans"}
            </button>
          </div>
        </div>
      </section>

      {/* Danger zone: account deletion */}
      <section className="mt-10" data-testid="account-danger-zone">
        <div className="flex items-center gap-2 mb-4"><ShieldAlert className="w-4 h-4 text-red-600" /><h2 className="font-display text-2xl text-[#1F3A5F]">Zone sensible</h2></div>
        <div className="app-card p-6 border border-red-200">
          <h3 className="font-bold text-[#1F3A5F]">Supprimer mon compte</h3>
          <p className="text-sm text-[#4A5568] mt-1">Cette action est définitive. Vos données personnelles seront effacées (RGPD art. 17). Vos crédits non utilisés ne sont pas restitués.</p>
          {!showDelete ? (
            <button
              onClick={() => setShowDelete(true)}
              data-testid="account-delete-btn"
              className="mt-4 px-4 py-2 rounded-xl border border-red-300 text-red-700 text-sm font-bold hover:bg-red-50"
            >
              Supprimer mon compte
            </button>
          ) : (
            <div className="mt-4 space-y-3">
              <label className="block">
                <span className="text-[11px] uppercase tracking-[0.18em] text-red-700 font-semibold">Confirmer avec votre mot de passe</span>
                <input
                  type="password"
                  value={deletePw}
                  onChange={(e) => setDeletePw(e.target.value)}
                  data-testid="account-delete-password"
                  className="mt-1 w-full bg-white border border-red-200 focus:border-red-500 focus:ring-2 focus:ring-red-100 outline-none rounded-lg px-4 py-3 text-sm text-[#1F3A5F]"
                />
              </label>
              <div className="flex gap-2">
                <button
                  onClick={() => { setShowDelete(false); setDeletePw(""); }}
                  disabled={deleting}
                  className="px-4 py-2 rounded-xl border border-[#1F3A5F]/15 text-sm font-bold text-[#1F3A5F]"
                  data-testid="account-delete-cancel"
                >
                  Annuler
                </button>
                <button
                  onClick={deleteAccount}
                  disabled={deleting}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-red-600 text-white text-sm font-bold hover:bg-red-700 disabled:opacity-60"
                  data-testid="account-delete-confirm"
                >
                  {deleting && <Loader2 className="w-4 h-4 animate-spin" />}
                  Supprimer définitivement
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function PushToggle() {
  const { t } = useT();
  const [status, setStatus] = useState({ supported: false, subscribed: false, permission: "default" });
  const [serverReady, setServerReady] = useState(null); // null = loading, false = not configured, true = ready
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    if (!isPushSupported()) return;
    getPushStatus().then(setStatus).catch(() => {});
    pushAvailability().then((a) => setServerReady(!!a.configured)).catch(() => setServerReady(false));
  }, []);

  const toggle = async () => {
    setBusy(true);
    try {
      if (status.subscribed) {
        await disablePush();
        toast.success(t("push.toastDisabled"));
      } else {
        await enablePush();
        toast.success(t("push.toastEnabled"));
      }
      const s = await getPushStatus();
      setStatus(s);
    } catch (e) {
      toast.error(e?.message || t("push.toastError"));
    } finally { setBusy(false); }
  };

  const testPush = async () => {
    setTesting(true);
    try {
      await sendTestPush();
      toast.success(t("push.toastTestSent"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("push.toastTestError"));
    } finally { setTesting(false); }
  };

  if (!isPushSupported()) {
    return (
      <div className="py-3 flex items-center justify-between opacity-60">
        <span className="text-sm text-[#1F3A5F]">{t("push.title")}</span>
        <span className="text-[10px] text-[#8B95A1] uppercase tracking-wider">{t("push.unsupported")}</span>
      </div>
    );
  }
  if (status.permission === "denied") {
    return (
      <div className="py-3">
        <div className="text-sm text-[#1F3A5F] font-bold">{t("push.title")}</div>
        <p className="text-xs text-[#8B95A1] mt-1">
          {t("push.denied")}
        </p>
      </div>
    );
  }
  if (serverReady === false) {
    return (
      <div className="py-3" data-testid="push-server-not-ready">
        <div className="text-sm text-[#1F3A5F] font-bold flex items-center gap-1.5">
          <BellOff className="w-4 h-4 text-[#8B95A1]" />
          {t("push.title")}
        </div>
        <p className="text-xs text-[#8B95A1] mt-1">
          Notifications push bientôt disponibles — le service est en cours de configuration sur cette version.
        </p>
      </div>
    );
  }

  return (
    <div className="py-3 flex items-center justify-between flex-wrap gap-3">
      <div>
        <div className="text-sm text-[#1F3A5F] font-bold flex items-center gap-1.5">
          {status.subscribed ? <BellRing className="w-4 h-4 text-[#E5276A]" /> : <BellOff className="w-4 h-4 text-[#8B95A1]" />}
          {t("push.title")}
        </div>
        <p className="text-xs text-[#8B95A1] mt-0.5">
          {t("push.description")}
        </p>
      </div>
      <div className="flex items-center gap-2">
        {status.subscribed && (
          <button
            onClick={testPush}
            disabled={testing}
            data-testid="push-test-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#1F3A5F]/15 text-xs font-bold text-[#1F3A5F] hover:border-[#E5276A] disabled:opacity-50"
          >
            {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            {t("push.test")}
          </button>
        )}
        <button
          onClick={toggle}
          disabled={busy}
          data-testid="push-toggle-btn"
          className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold transition-colors disabled:opacity-50 ${
            status.subscribed
              ? "border-2 border-[#E5276A]/40 text-[#E5276A] hover:bg-[#FCE4ED]"
              : "bg-[#E5276A] text-white hover:bg-[#C01661]"
          }`}
        >
          {busy && <Loader2 className="w-4 h-4 animate-spin" />}
          {status.subscribed ? t("push.disable") : t("push.enable")}
        </button>
      </div>
    </div>
  );
}

function OrderRow({ order, onView }) {
  const paid = order.payment_status === "paid";
  const shipped = !!order.gelato_order_id;
  const failed = !!order.gelato_error;
  const date = (order.created_at || "").slice(0, 10);
  const itemsCount = (order.items || []).reduce((s, i) => s + (i.quantity || 0), 0);
  const previewImg = (order.items?.[0]?.product_title) ? null : null; // No image in mine list
  return (
    <button onClick={onView} data-testid={`account-order-${order.id}`}
      className="w-full flex items-center gap-3 py-3 text-left hover:bg-[#FBF7F3] rounded-lg px-2 -mx-2 transition-colors">
      <div className="w-10 h-10 rounded-xl bg-[#FCE4ED] flex items-center justify-center shrink-0">
        {failed ? <AlertCircle className="w-5 h-5 text-[#E5276A]" />
         : shipped ? <Truck className="w-5 h-5 text-[#22C55E]" />
         : paid ? <Package className="w-5 h-5 text-[#E5276A]" />
         : <ShoppingBag className="w-5 h-5 text-[#8B95A1]" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-bold text-[#1F3A5F] text-sm">Commande #{(order.id || "").slice(0, 8)}</span>
          <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
            failed ? "bg-[#FCE4ED] text-[#C01661]"
            : shipped ? "bg-[#DCFCE7] text-[#15803D]"
            : paid ? "bg-[#FEF3C7] text-[#92400E]"
            : "bg-[#F3F4F6] text-[#6B7280]"
          }`}>
            {failed ? "À traiter" : shipped ? "En production" : paid ? "Payé" : "En attente"}
          </span>
        </div>
        <div className="text-xs text-[#8B95A1] mt-0.5">
          {date} · {itemsCount} article{itemsCount > 1 ? "s" : ""} · {Number(order.total_eur || 0).toFixed(2)} €
        </div>
      </div>
      <ChevronRight className="w-4 h-4 text-[#8B95A1] shrink-0" />
    </button>
  );
}
